import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { FILE_UPLOAD } from '@src/app/features/shared/constants/file-upload';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ApiFileCenterService,
  AppConfig,
  SystemDialogService,
  UserProfileService
} from 'sigv-library';
import { ErrorDialogMessage } from 'sigv-library/model';
import { getImagePreviewSrcByBlob } from 'sigv-library/util';
import { EKYCService } from './services/ekyc.service';

@Component({
  selector: 'app-ekyc',
  templateUrl: './ekyc.component.html',
  styleUrls: ['./ekyc.component.scss'],
})
export class EKYCComponent implements OnInit, OnDestroy {
  /** KH國家限定 */
  @Input() showEKYCButton = false;
  @Input() submissionNo = -1;
  @Input() isDialogMode = true;
  @Input() needWebcam = false;
  @Input() needScore = false;
  @Input() verifyType = '';
  @Output() scoreEvent = new EventEmitter();
  @Output() imgEvent = new EventEmitter();
  isShowQueryDialog = false;
  dateFormat = '';
  ekycScore = 0;
  facingMode = 'environment';

  videoWidth = Math.min((80 * window.innerWidth) / 100, 500);
  videoHeight = Math.min((80 * window.innerWidth) / 100, 500);

  validationResults: {
    status: boolean | null;
    date: string;
  } = {
    status: null, // api 回傳結果
    date: '', // api 回傳結果
  };

  eKYCRecords: [
    {
      title: string;
      name: string;
      img: any;
      src: SafeUrl | null;
      blob: Blob | null;
      showCam: boolean;
      stream: MediaStream | null;
      showUpload: boolean;
    },
    {
      title: string;
      name: string;
      img: any;
      src: SafeUrl | null;
      blob: Blob | null;
      showCam: boolean;
      stream: MediaStream | null;
      showUpload: boolean;
    }
  ] = [
    {
      title: 'Common.Validate.EKYCIDCard',
      name: 'IDcard',
      img: null,
      src: null,
      blob: null,
      showCam: false,
      stream: null,
      showUpload: true,
    },
    {
      title: 'Common.Validate.EKYCPhoto',
      name: 'Photo',
      img: null,
      src: null,
      blob: null,
      showCam: false,
      stream: null,
      showUpload: this.verifyType != 'Commencement',
    },
  ];

  private readonly destroy$ = new Subject();

  constructor(
    appConfig: AppConfig,
    private apiFileCenterService: ApiFileCenterService,
    private userProfileService: UserProfileService,
    private sanitizer: DomSanitizer,
    private dialogService: SystemDialogService,
    private translateService: TranslateService,
    private eKYCService: EKYCService
  ) {
    this.dateFormat = appConfig.format.datetime;
  }

  ngOnInit(): void {
    this.initial();
    this.GetLastEKYCRecord();
  }

  async startCamera(recordName: string, videoElement: any, canvas: any) {
    try {
      this.eKYCRecords.forEach((x) => {
        if (x.name !== recordName) {
          x.showCam = false;
          if (x.stream) {
            x.stream.getTracks().forEach((track) => track.stop());
            videoElement.pause();
            videoElement.srcObject = null;
          }
        }
      });
      const record = this.eKYCRecords.find((x) => x.name === recordName);
      if (record) {
        record.showCam = !record?.showCam;
        if (record.showCam) {
          console.log('Trigger Cam' + record);
          record.stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: this.facingMode, // 'environment' 表示使用後置鏡頭，'user' 表示使用前置鏡頭
            },
          });
          videoElement.srcObject = record.stream;
          videoElement.load();
          videoElement.play();
        } else {
          if (record.stream) {
            record.stream.getTracks().forEach((track) => track.stop());
            videoElement.pause();
            videoElement.srcObject = null;
          }
        }
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  }

  async switchCamera(recordName: string, videoElement: any) {
    try {
      const record = this.eKYCRecords.find((x) => x.name === recordName);
      if (record) {
        if (record.stream) {
          record.stream.getTracks().forEach((track) => track.stop());
          videoElement.pause();
          videoElement.srcObject = null;
        }
        if (record.showCam) {
          if (this.facingMode === 'environment') {
            this.facingMode = 'user';
          } else if (this.facingMode === 'user') {
            this.facingMode = 'environment';
          }
          console.log('Switch Cam:' + this.facingMode);
          record.stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: this.facingMode, // 'environment' 表示使用後置鏡頭，'user' 表示使用前置鏡頭
            },
          });
          videoElement.srcObject = record.stream;
          videoElement.load();
          videoElement.play();
        }
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  }

  capturePhoto(recordName: string, videoElement: any, canvas: any) {
    const context = canvas.getContext('2d');
    context.canvas.width = videoElement.videoWidth;
    context.canvas.height = videoElement.videoHeight;
    context.drawImage(
      videoElement,
      0,
      0,
      videoElement.videoWidth,
      videoElement.videoHeight
    );

    canvas.toBlob((blob: Blob) => {
      const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
      // 接下來的處理
      this.apiFileCenterService
        .uploadFile({
          domain: 'WebSubmission',
          subDomain: 'EKYC',
          creator: this.userProfileService.currentUserValue?.userNo || '',
          description: 'WebSubmission EKYC ' + recordName + ' File',
          file,
          sortIndex: 1,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          var record = this.eKYCRecords.find((x) => x.name === recordName);
          if (record) {
            record.img = result;
            record.src = this.createSaveUrl(file);
            record.blob = file;
            console.log('Captured photo:', record.src);
            this.imgEvent.emit({
              idImageGuid: this.eKYCRecords.find((x) => x.name === 'IDcard')?.img,
              faceImgGuid: this.eKYCRecords.find((x) => x.name === 'Photo')?.img,
            });
          }
        });
    }, 'image/jpeg');
  }

  initial(): void {
    this.validationResults = {
      status: null,
      date: '',
    };
    this.eKYCRecords = [
      {
        title: 'Common.Validate.EKYCIDCard',
        name: 'IDcard',
        img: null,
        src: null,
        blob: null,
        showCam: false,
        stream: null,
        showUpload: true,
      },
      {
        title: 'Common.Validate.EKYCPhoto',
        name: 'Photo',
        img: null,
        src: null,
        blob: null,
        showCam: false,
        stream: null,
        showUpload: this.verifyType != 'Commencement',
      },
    ];
  }

  GetLastEKYCRecord() {
    if (this.submissionNo == 0) {
      return;
    }
    this.eKYCService
      .GetLastEKYCRecord({
        submissionNo: this.submissionNo,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((record) => {
        if (record.data) {
          if (record.data.verifyType == this.verifyType) {
            this.imgEvent.emit({
              idImageGuid: record.data?.idImageGuid,
              faceImgGuid: record.data?.faceImgGuid
            });
            this.validationResults.date = record.data.recordDate!;
            this.validationResults.status = record.data.verifyStatus!;
            this.ekycScore = record.data.faceDocumentScore ?? 0;
            this.apiFileCenterService
              .getFileDownloadAsync({
                fileDetailId: record.data.faceImgGuid!,
                downloader: this.userProfileService.currentUserValue?.userNo!,
              })
              .pipe(takeUntil(this.destroy$))
              .subscribe((photoResponse: any) => {
                const url = photoResponse
                  ? this.createSaveUrl(photoResponse)
                  : '';

                var photo = this.eKYCRecords.find((x) => x.name === 'Photo')!;
                photo.img = record?.data?.faceImgGuid;
                photo.src = url;
                photo.blob = photoResponse;
              });
            this.apiFileCenterService
              .getFileDownloadAsync({
                fileDetailId: record.data.idImageGuid!,
                downloader: this.userProfileService.currentUserValue?.userNo!,
              })
              .pipe(takeUntil(this.destroy$))
              .subscribe((idCardResponse: any) => {
                const url = idCardResponse
                  ? this.createSaveUrl(idCardResponse)
                  : '';
    
                var idCard = this.eKYCRecords.find((x) => x.name === 'IDcard')!;
                idCard.img = record?.data?.idImageGuid;
                idCard.src = url;
                idCard.blob = idCardResponse;
              });
          }
          if (record.data.verifyStatus == null) {
            return;
          }
        }
      });
  }

  CancelDialog() {
    this.isShowQueryDialog = false;

    this.eKYCRecords.forEach((x) => {
      x.showCam = false;
    });
  }

  /**
   * 開啟彈跳視窗
   */
  showQueryDialog() {
    this.isShowQueryDialog = true;

    if (this.submissionNo > 0) {
      this.initial();
      this.GetLastEKYCRecord();
    }
  }

  /**
   * 執行驗證
   */
  async Validate(): Promise<void> {
    this.validationResults.status = null;
    var errorMessageArr: ErrorDialogMessage[] = [];
    /** 驗證檔案是否上傳 */
    this.eKYCRecords.forEach((r) => {
      if (!r.img) {
        errorMessageArr.push({
          description: this.translateService.instant(
            'Common.Validate.EKYCValidateUpload',
            {
              type: this.translateService.instant(r?.title!),
            }
          ),
        });
      }
    });

    if (errorMessageArr.length > 0) {
      this.dialogService.showErrorDialog(errorMessageArr);
      return;
    } else {
      this.eKYCService
        .VerifyInfoAndFaceAndIdCard({
          idImage: await this.ConvertToBase64(
            this.eKYCRecords.find((x) => x.name === 'IDcard')?.blob!
          ),
          faceImg: await this.ConvertToBase64(
            this.eKYCRecords.find((x) => x.name === 'Photo')?.blob!
          ),
          idImageGuid: this.eKYCRecords.find((x) => x.name === 'IDcard')?.img,
          faceImgGuid: this.eKYCRecords.find((x) => x.name === 'Photo')?.img,
          submissionNo: this.submissionNo,
          verifyType: this.verifyType,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe((x) => {
          var result = x.data;

          if (result) {
            if (!result.verifyStatus) {
              this.validationResults.status = result.verifyStatus;
              this.validationResults.date = result.recordDate!;
              this.scoreEvent.emit({
                faceMoiScore: null,
                score: null,
                faceDocumentScore: null,
                recordId: result.recordId,
                verifyStatus: result.verifyStatus
              });
            } else {
              this.validationResults.status = result.verifyStatus;
              this.validationResults.date = result.recordDate!;
              this.ekycScore = result.faceDocumentScore ?? 0
              this.scoreEvent.emit({
                faceMoiScore: result.faceMoiScore,
                score: result.score,
                faceDocumentScore: result.faceDocumentScore,
                recordId: result.recordId,
                verifyStatus: result.verifyStatus
              });
            }
          }
        });
    }
  }

  ConvertToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event: ProgressEvent<FileReader>) => {
        if (event.target && event.target.result) {
          var base64String: string = event.target.result as string;
          const regex = /data:.*base64,/;
          var outputString = base64String.replace(regex, '');
          resolve(outputString);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read Blob as Base64.'));
      };

      reader.readAsDataURL(blob);
    });
  }

  /**
   * 選擇上傳檔案
   */
  selectedUploadSignatureFile(target: any, recordName: string): void {
    this.validationResults.status = null;
    const file: File = target.files[0];
    const acceptFileExtensions = target.accept;
    const record = this.eKYCRecords.find((x) => x.name === recordName);

    if (file == undefined) {
      return;
    }

    // Validate File extension
    const isFileTypeValidate = this.validateAcceptFileType(
      [file.name],
      acceptFileExtensions
    );
    if (!isFileTypeValidate) {
      this.dialogService.showErrorDialog([
        {
          description: this.translateService.instant(
            'Common.Validate.EKYCValidateAccept',
            {
              type: this.translateService.instant(record?.title!),
            }
          ),
        },
      ]);
      return;
    }

    // Validate File Size
    const isFileSizeValidate = this.validateFilesMaxSize([file.size]);
    if (!isFileSizeValidate) {
      this.dialogService.showErrorDialog([
        {
          description: this.translateService.instant(
            'Common.Validate.EKYCValidateSize',
            {
              type: this.translateService.instant(record?.title!),
            }
          ),
        },
      ]);
      return;
    }

    this.apiFileCenterService
      .uploadFile({
        domain: 'WebSubmission',
        subDomain: 'EKYC',
        creator: this.userProfileService.currentUserValue?.userNo || '',
        description: 'WebSubmission EKYC ' + recordName + ' File',
        file,
        sortIndex: 1,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (result: any) => {
        var record = this.eKYCRecords.find((x) => x.name === recordName);
        if (record) {
          record.img = result;
          record.src = this.createSaveUrl(file);
          record.blob = file;
          this.imgEvent.emit({
          idImageGuid: this.eKYCRecords.find((x) => x.name === 'IDcard')?.img,
          faceImgGuid: this.eKYCRecords.find((x) => x.name === 'Photo')?.img,
          submissionNo: this.submissionNo,
          verifyType: this.verifyType,
        });
        }
      });
  }

  /**
   * Validate file size
   */
  private validateFilesMaxSize(fileSizes: number[]): boolean {
    if (fileSizes && fileSizes.length > 0) {
      var min = FILE_UPLOAD.eKYCFileMinSizeLimit * 1024;
      var max = FILE_UPLOAD.eKYCFileMaxSizeLimit * (1024 * 1024);
      return !fileSizes.find((size) => !(size >= min && size <= max));
    } else {
      return true;
    }
  }

  /**
   * Validate file extension
   * @param fileNames
   * @param accept accept file extension split by comma
   * @returns
   */
  private validateAcceptFileType(fileNames: string[], accept: string): boolean {
    const accepts = accept
      .split(',')
      .map((extensionName) => {
        if (extensionName) {
          return extensionName.replace(/\./gm, '').trim();
        } else {
          return '';
        }
      })
      .filter((e) => !!e);
    if (accepts && accepts.length > 0) {
      const ruleString = `\\.(${accepts.join('|')})+$`;
      const regex = new RegExp(ruleString);

      return fileNames.every((fileName) => {
        if (fileName) {
          const checkFileName = fileName.toLocaleLowerCase();
          return regex.test(checkFileName);
        } else {
          return false;
        }
      });
    }
    return true;
  }

  /**
   * 轉換File to Url
   */
  createSaveUrl(file: Blob): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(
      getImagePreviewSrcByBlob(file)
    );
  }

  ngOnDestroy(): void {
    this.eKYCRecords.forEach((x) => {
      x.showCam = false;
      if (x.stream) {
        x.stream.getTracks().forEach((track) => track.stop());
      }
    });
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatScore(score: number): string {
    return (Math.floor(score * 10000) / 100).toFixed(2);
  }
}
